<template>
  <v-card>
    <v-card-text>
      <span class="text-h5">Скидання пароля</span>
      <template v-if="step === 1">
        <v-form
          @submit.prevent="onSubmit"
          :disabled="isLoading"
          id="email-form"
        >
          <v-text-field
            type="email"
            v-model="form.email"
            label="Email"
            :error-messages="emailErrors"
            @input="$v.form.email.$touch()"
            @blur="$v.form.email.$touch()"
          />
        </v-form>
      </template>
      <template v-else-if="step === 2">
        <v-form @submit.prevent="onSubmit" :disabled="isLoading" id="code-form">
          <v-text-field
            type="text"
            v-model="form.code"
            label="Код"
            pattern="[0-9]*"
            :error-messages="codeErrors"
            @input="$v.form.code.$touch()"
            @blur="$v.form.code.$touch()"
          />
        </v-form>
      </template>
      <template v-else-if="step === 3">
        <v-form
          @submit.prevent="onSubmit"
          :disabled="isLoading"
          id="password-form"
        >
          <v-text-field
            :type="passwordShown ? 'text' : 'password'"
            v-model="form.password"
            label="Новий пароль"
            :append-icon="passwordShown ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="passwordShown = !passwordShown"
            :error-messages="passwordErrors"
            @input="$v.form.password.$touch()"
            @blur="$v.form.password.$touch()"
          />
        </v-form>
      </template>
    </v-card-text>
    <v-card-actions>
      <v-container>
        <v-row>
          <v-col cols="6" class="d-flex align-start flex-column">
            <router-link class="black--text" :to="{ name: 'Login' }"
              >Вхід</router-link
            >
            <router-link class="black--text" :to="{ name: 'Register' }"
              >Реєстрація</router-link
            >
          </v-col>
          <v-col cols="6" class="d-flex align-end flex-column">
            <v-btn
              v-if="step === 1"
              type="submit"
              color="primary"
              :loading="isLoading"
              form="email-form"
              >Далі</v-btn
            >
            <v-btn
              v-else-if="step === 2"
              type="submit"
              color="primary"
              :loading="isLoading"
              form="code-form"
              >Далі</v-btn
            >
            <v-btn
              v-else-if="step === 3"
              type="submit"
              color="primary"
              :loading="isLoading"
              form="password-form"
              >Скинути пароль</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  email,
  numeric,
  minLength,
  maxLength,
} from "vuelidate/lib/validators";
import { mapActions } from "vuex";
import {
  sendResetPasswordCode,
  validateResetPasswordCode,
  resetPassword,
} from "@/api/auth";
import { getErrorMessage, getFieldErrorMessages } from "@/helpers/errors";

export default {
  name: "ResetPassword",
  mixins: [validationMixin],
  validations: {
    form: {
      email: { required, email },
      code: {
        required,
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
      password: { required, minLength: minLength(8), maxLength: maxLength(64) },
    },
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      passwordShown: false,
      form: {
        email: null,
        code: null,
        password: null,
      },
    };
  },
  computed: {
    emailErrors() {
      return getFieldErrorMessages(this.$v.form.email, {
        required: "Введіть email",
        email: "Неправильний email",
      });
    },
    codeErrors() {
      return getFieldErrorMessages(this.$v.form.code, {
        required: "Введіть код",
        numeric: "Код має бути числовим",
        minLength: "Код має дорівнювати 6 символам",
        maxLength: "Код має дорівнювати 6 символам",
      });
    },
    passwordErrors() {
      return getFieldErrorMessages(this.$v.form.password, {
        required: "Введіть новий пароль",
        minLength: "Пароль має бути довшим або дорівнювати 8 символам",
        maxLength: "Пароль має бути коротшим або дорівнювати 64 символам",
      });
    },
  },
  methods: {
    ...mapActions(["showMessage", "login"]),
    async onSubmit() {
      if (this.step === 1) {
        this.$v.form.email.$touch();
        if (!this.$v.form.email.$invalid) {
          try {
            this.isLoading = true;
            await sendResetPasswordCode({ email: this.form.email });
            this.step++;
            this.showMessage(
              `Код скидання пароля надіслано на ${this.form.email}. Термін дії коду 30 хвилин`
            );
          } catch (error) {
            this.showMessage(getErrorMessage(error));
          } finally {
            this.isLoading = false;
          }
        }
      } else if (this.step === 2) {
        this.$v.form.code.$touch();
        if (!this.$v.form.code.$invalid) {
          try {
            this.isLoading = true;
            await validateResetPasswordCode({
              email: this.form.email,
              code: this.form.code,
            });
            this.step++;
          } catch (error) {
            this.showMessage(getErrorMessage(error));
          } finally {
            this.isLoading = false;
          }
        }
      } else if (this.step === 3) {
        this.$v.form.password.$touch();
        if (!this.$v.form.password.$invalid) {
          try {
            this.isLoading = true;
            await resetPassword(this.form);
            await this.login({
              email: this.form.email,
              password: this.form.password,
            });
            await this.$router.push({ name: "Home" });
            this.showMessage("Пароль успішно скинуто");
          } catch (error) {
            this.showMessage(getErrorMessage(error));
          } finally {
            this.isLoading = false;
          }
        }
      }
    },
  },
};
</script>
